// src/pages/profile/ManageAvailability.tsx
// 🆕 UPDATED: Emits profile update events to refresh ProfileContext cache
// ✅ FIXED: Prevents overlapping time slots on the same day

import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCheckbox,
  IonCard,
  IonCardContent,
  IonToast,
  IonSpinner,
} from '@ionic/react';
import { add, closeCircle } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { profileService } from '../../services/profileService';
import { Availability, DAYS_OF_WEEK } from '../../types/profile';
import { emitProfileUpdate } from '../../contexts/ProfileContext';
import './ManageAvailability.css';

interface TimeSlot {
  day: number;
  startTime: string;
  endTime: string;
}

const ManageAvailability: React.FC = () => {
  const [availability, setAvailability] = useState<Availability[]>([]);
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    loadAvailability();
  }, []);

  const loadAvailability = async () => {
    const data = await profileService.getUserAvailability();
    setAvailability(data);

    // Convert to TimeSlot format for editing
    const converted = data.map(a => ({
      day: a.day_of_week,
      startTime: a.start_time,
      endTime: a.end_time,
    }));
    setSlots(converted);
    setLoading(false);
  };

  const addSlot = () => {
    setSlots([...slots, { day: 0, startTime: '18:00', endTime: '22:00' }]);
  };

  const removeSlot = (index: number) => {
    setSlots(slots.filter((_, i) => i !== index));
  };

  const updateSlot = (index: number, field: keyof TimeSlot, value: any) => {
    const updated = [...slots];
    updated[index] = { ...updated[index], [field]: value };
    setSlots(updated);
  };

  // Helper function to convert time string to minutes
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Check if two time slots overlap
  const slotsOverlap = (slot1: TimeSlot, slot2: TimeSlot): boolean => {
    if (slot1.day !== slot2.day) return false;

    const start1 = timeToMinutes(slot1.startTime);
    const end1 = timeToMinutes(slot1.endTime);
    const start2 = timeToMinutes(slot2.startTime);
    const end2 = timeToMinutes(slot2.endTime);

    // Check if times overlap
    return start1 < end2 && start2 < end1;
  };

  // Validate that no slots overlap
  const validateSlots = (): { valid: boolean; error?: string } => {
    // Check for invalid times (end before start)
    for (let i = 0; i < slots.length; i++) {
      const start = timeToMinutes(slots[i].startTime);
      const end = timeToMinutes(slots[i].endTime);
      if (start >= end) {
        return {
          valid: false,
          error: `Slot ${i + 1}: End time must be after start time`
        };
      }
    }

    // Check for overlaps
    for (let i = 0; i < slots.length; i++) {
      for (let j = i + 1; j < slots.length; j++) {
        if (slotsOverlap(slots[i], slots[j])) {
          const dayName = DAYS_OF_WEEK.find(d => d.value === slots[i].day)?.name || 'Unknown';
          return {
            valid: false,
            error: `You have overlapping time slots on ${dayName}. Please adjust your times.`
          };
        }
      }
    }

    return { valid: true };
  };

  const handleSave = async () => {
    // Validate slots before saving
    const validation = validateSlots();
    if (!validation.valid) {
      setToastMessage(validation.error || 'Invalid time slots');
      setShowToast(true);
      return;
    }

    setSaving(true);

    const availabilityData = slots.map(slot => ({
      day_of_week: slot.day,
      start_time: slot.startTime,
      end_time: slot.endTime,
    }));

    const result = await profileService.setAvailability(availabilityData);

    if (result.success) {
      // 🆕 Emit profile update event
      emitProfileUpdate();

      // Refresh the availability data to ensure state is updated
      await loadAvailability();

      setToastMessage('Availability updated!');
      setShowToast(true);
    } else {
      setToastMessage(result.error || 'Failed to update');
      setShowToast(true);
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton />
            </IonButtons>
            <IonTitle>Manage Availability</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
            <IonSpinner />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/profile/edit" />
          </IonButtons>
          <IonTitle>Manage Availability</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleSave} disabled={saving}>
              {saving ? <IonSpinner /> : 'Save'}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent
        style={{
          '--padding-bottom': 'calc(env(safe-area-inset-bottom) + 80px)'
        }}
      >
        <div className="availability-container">
          {slots.length === 0 ? (
            <IonCard>
              <IonCardContent>
                <p>Add time slots when you're available to play games</p>
              </IonCardContent>
            </IonCard>
          ) : (
            <IonList>
              {slots.map((slot, index) => (
                <IonItem key={index}>
                  <div className="slot-row">
                    <select
                      className="day-select"
                      value={slot.day}
                      onChange={(e) => updateSlot(index, 'day', parseInt(e.target.value))}
                    >
                      {DAYS_OF_WEEK.map((day, i) => (
                        <option key={i} value={day.value}>
                          {day.name}
                        </option>
                      ))}
                    </select>

                    <input
                      type="time"
                      className="time-input"
                      value={slot.startTime}
                      onChange={(e) => updateSlot(index, 'startTime', e.target.value)}
                    />

                    <span>to</span>

                    <input
                      type="time"
                      className="time-input"
                      value={slot.endTime}
                      onChange={(e) => updateSlot(index, 'endTime', e.target.value)}
                    />

                    <IonButton
                      fill="clear"
                      color="danger"
                      onClick={() => removeSlot(index)}
                    >
                      <IonIcon icon={closeCircle} />
                    </IonButton>
                  </div>
                </IonItem>
              ))}
            </IonList>
          )}

          <IonButton expand="block" onClick={addSlot} className="ion-margin-top">
            <IonIcon slot="start" icon={add} />
            Add Time Slot
          </IonButton>
        </div>
      </IonContent>

      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={2000}
        position="top"
        aria-live="polite"
      />
    </IonPage>
  );
};

export default ManageAvailability;